/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/_modes/focus-on-hash.tsx`.
 *
 * The `"use client"` directive is DROPPED, not carried: this host has no server
 * component graph, so the directive is inert here and keeping it would imply a
 * boundary that does not exist. Every other line is identical — it only touches
 * `window`/`document`, which is what made it a client island in the first place.
 *
 * Reached from 13 root pages across 6 sections in the Next tree, all through
 * `_modes/mode-page.tsx`. In this host each ported mode route mounts it directly,
 * so the fan-in is real rather than inherited.
 */

import { useEffect } from "react";

/**
 * Consumes the `#focus-<uid>` hash that a Play Dive-in emits (Three-Layer PRD
 * §2.5/§2.6 — "lands on the object's surface with it in context"). On mount and on
 * hashchange it scrolls the matching `id="focus-<uid>"` element into view and
 * flashes a transient ring, so Dive-in lands the user ON the object, not merely on
 * its list. A no-op when the hash isn't a focus anchor or no row matches. Mounted
 * once per mode view (ModePage); client-only (reads `location`).
 */
export function FocusOnHash() {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const RING = ["ring-2", "ring-primary", "ring-offset-2", "rounded-md"];
    const apply = () => {
      const m = /^#focus-(.+)$/.exec(window.location.hash);
      if (!m) return;
      const el = document.getElementById(`focus-${m[1]}`);
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.classList.add(...RING);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove(...RING), 2200);
    };
    // Defer the first pass a tick so the target list has rendered.
    const raf = requestAnimationFrame(apply);
    window.addEventListener("hashchange", apply);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("hashchange", apply);
      if (timer) clearTimeout(timer);
    };
  }, []);
  return null;
}
